import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';

import type { MapViewMode } from '@/components/map/utils/basemaps';
import { GLOBE_SKY } from '@/components/map/utils/globeSky';

/**
 * Manages all globe-mode side-effects on the underlying MapLibre instance:
 *
 * - Switches the projection (globe ↔ mercator) imperatively, working around
 *   react-map-gl's skipMethods list which silently ignores prop changes for
 *   setProjection and setMinZoom after initial mount.
 *
 * - Guards against MapLibre v5's camera/transform race condition: the camera
 *   helper updates synchronously while map.transform updates in the next render
 *   frame. Interaction handlers are disabled for that window to prevent the
 *   "getRayDirectionFromPixel Not implemented" crash.
 *
 * - Applies the space-sky atmosphere when in globe mode. The globe is lit
 *   evenly: no day/night or twilight overlay is added, so the basemap and
 *   network assets remain readable everywhere.
 *
 * - Re-applies all of the above after every `style.load` event so that basemap
 *   switches (which fully reload the style) do not reset these settings.
 */
export function useGlobeMode(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    viewMode: MapViewMode,
    canZoomAndDragRef: RefObject<boolean>,
): void {
    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        // Disable interaction handlers while setProjection transitions the camera helper
        // and transform (they update at different times in MapLibre v5).
        mlMap.scrollZoom.disable();
        mlMap.dragPan.disable();
        mlMap.touchZoomRotate.disable();
        mlMap.doubleClickZoom.disable();

        // Both setProjection and setMinZoom are in react-map-gl's skipMethods so prop
        // changes are not forwarded — call them imperatively on every viewMode change.
        mlMap.setProjection({ type: viewMode === 'globe' ? 'globe' : 'mercator' });
        mlMap.setMinZoom(0);

        let restored = false;
        const restoreHandlers = () => {
            if (restored) return;
            restored = true;
            if (canZoomAndDragRef.current) {
                mlMap.scrollZoom.enable();
                mlMap.dragPan.enable();
                mlMap.touchZoomRotate.enable();
                mlMap.doubleClickZoom.enable();
            }
        };

        // Re-enable after the next rendered frame (transform is consistent by then).
        void mlMap.once('render', restoreHandlers);
        // Fallback in case the render event is delayed (e.g. tab backgrounded).
        const restoreTimer = setTimeout(restoreHandlers, 500);

        // applyEffects runs immediately AND on every subsequent style.load so that
        // basemap switches (which fully reload the style) re-apply all settings.
        const applyEffects = () => {
            mlMap.setProjection({ type: viewMode === 'globe' ? 'globe' : 'mercator' });
            mlMap.setMinZoom(0);

            try {
                mlMap.setSky(viewMode === 'globe' ? GLOBE_SKY : {});
            } catch {
                // Sky may not be supported in all MapLibre builds / base styles.
            }
        };

        applyEffects();
        mlMap.on('style.load', applyEffects);

        return () => {
            mlMap.off('render', restoreHandlers);
            clearTimeout(restoreTimer);
            mlMap.off('style.load', applyEffects);
            // Ensure handlers are not left disabled if the effect tears down before
            // the render fires (e.g. React StrictMode double-invoke in development).
            restoreHandlers();
        };
    }, [viewMode, isMapLoaded, mapRef, canZoomAndDragRef]);
}
